import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, 
  Gavel, 
  User, 
  Calendar,
  ChevronLeft,
  ChevronRight,
  XCircle,
  Eye,
  Filter,
  Package,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [selectedBid, setSelectedBid] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [bidDetails, setBidDetails] = useState(null);

  useEffect(() => {
    fetchBids();
  }, [pagination.skip, statusFilter]);

  const fetchBids = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit
      });
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API_URL}/api/admin/bids?${params}`);
      setBids(response.data.bids);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error('Failed to fetch bids');
    } finally {
      setLoading(false);
    }
  };

  const fetchBidDetails = async (bidId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/bids/${bidId}`);
      setBidDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch bid details');
    }
  };

  const handleViewBid = async (bid) => {
    setSelectedBid(bid);
    setBidDetails(null);
    setShowDetailsModal(true);
    await fetchBidDetails(bid.id);
  };

  const handleRejectBid = (bid) => {
    setSelectedBid(bid);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmRejectBid = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/bids/${selectedBid.id}/reject`, {
        reason: rejectReason
      });
      toast.success('Bid rejected successfully');
      setShowRejectModal(false);
      fetchBids();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject bid');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      submitted: 'bg-blue-500/20 text-blue-400',
      countered: 'bg-amber-500/20 text-amber-400',
      accepted: 'bg-emerald-500/20 text-emerald-400',
      rejected: 'bg-red-500/20 text-red-400',
      withdrawn: 'bg-slate-500/20 text-slate-400',
      not_selected: 'bg-slate-500/20 text-slate-400'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-slate-500/20 text-slate-400'}`}>
        {status?.replace('_', ' ')?.toUpperCase()}
      </span>
    );
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Bid Management
        </h1>
        <p className="text-slate-400">View and manage all platform bids</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-2xl p-4 mb-6 border border-slate-700 flex flex-col md:flex-row gap-4">
        <div className="flex-1 flex items-center gap-2 text-slate-400">
          <Gavel className="w-5 h-5" />
          <span>Total: {pagination.total} bids</span>
        </div>
        <div className="relative">
          <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, skip: 0 }));
            }}
            className="pl-12 pr-8 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none cursor-pointer"
            data-testid="status-filter-select"
          >
            <option value="">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="countered">Countered</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="withdrawn">Withdrawn</option>
            <option value="not_selected">Not Selected</option>
          </select>
        </div>
      </div>

      {/* Bids Table */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : bids.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <Gavel className="w-12 h-12 mx-auto mb-2 text-slate-600" />
            <p>No bids found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Bidder</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Listing</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Offer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Bid Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {bids.map((bid) => (
                  <tr 
                    key={bid.id} 
                    className="hover:bg-slate-700/30 transition-colors"
                    data-testid={`bid-row-${bid.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">
                            {bid.bidder_name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{bid.bidder_name || 'Unknown'}</p>
                          <p className="text-xs text-slate-400 truncate">{bid.bidder_email || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="min-w-0">
                        <p className="text-white text-sm truncate max-w-xs" title={bid.listing_title}>
                          {bid.listing_title || 'Unknown'}
                        </p>
                        <p className="text-xs text-slate-400">Seller: {bid.seller_name || 'Unknown'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {bid.items?.length > 0 && (
                          <span className="text-sm text-slate-300 flex items-center gap-1">
                            <Package className="w-3 h-3 text-emerald-400" />
                            {bid.items.length} item{bid.items.length > 1 ? 's' : ''}
                          </span>
                        )}
                        {bid.money_amount > 0 && (
                          <span className="text-sm text-emerald-400 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(bid.money_amount)}
                          </span>
                        )}
                        {(!bid.items?.length && !bid.money_amount) && (
                          <span className="text-sm text-slate-500">No offer details</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        {formatDate(bid.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(bid.status)}
                      {bid.admin_rejected && (
                        <span className="block text-xs text-red-400 mt-1">Admin rejected</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewBid(bid)}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="View Details"
                          data-testid={`view-bid-${bid.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {!['accepted', 'rejected', 'withdrawn'].includes(bid.status) && (
                          <button
                            onClick={() => handleRejectBid(bid)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Reject Bid"
                            data-testid={`reject-bid-${bid.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        <a
                          href={`/listing/${bid.listing_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="View Listing"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} bids
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              disabled={pagination.skip === 0}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-slate-400 text-sm">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              disabled={pagination.skip + pagination.limit >= pagination.total}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Bid Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto" data-testid="bid-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                <Gavel className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  Bid Details
                  {selectedBid && getStatusBadge(selectedBid?.status)}
                </div>
                <p className="text-sm text-slate-400">on {selectedBid?.listing_title}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {bidDetails ? (
            <div className="mt-4 space-y-6">
              {/* Bidder & Seller Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-2">Bidder</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{bidDetails.bidder?.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-white">{bidDetails.bidder?.name}</p>
                      <p className="text-xs text-slate-400">{bidDetails.bidder?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-2">Seller</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{bidDetails.seller?.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-white">{bidDetails.seller?.name}</p>
                      <p className="text-xs text-slate-400">{bidDetails.seller?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Offer Details */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <p className="text-slate-400 text-sm mb-3">Offer Details</p>
                <div className="space-y-3">
                  {bidDetails.bid?.items?.length > 0 && (
                    <div>
                      <p className="text-white text-sm font-medium mb-2">Items Offered ({bidDetails.bid.items.length})</p>
                      <div className="space-y-2">
                        {bidDetails.bid.items.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-slate-600/30 rounded-lg p-2">
                            {item.images?.[0] ? (
                              <img src={item.images[0]} alt={item.title} className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 bg-slate-600 rounded flex items-center justify-center">
                                <Package className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm truncate">{item.title}</p>
                              {item.estimated_value && (
                                <p className="text-xs text-emerald-400">Est. {formatCurrency(item.estimated_value)}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {bidDetails.bid?.money_amount > 0 && (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <DollarSign className="w-5 h-5" />
                      <span className="text-lg font-bold">{formatCurrency(bidDetails.bid.money_amount)}</span>
                      <span className="text-slate-400 text-sm">cash offered</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Bid Message */}
              {bidDetails.bid?.message && (
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-2">Bidder's Message</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{bidDetails.bid.message}</p>
                </div>
              )}

              {/* Counter Offer */}
              {(bidDetails.bid?.counter_message || bidDetails.bid?.counter_amount) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <p className="text-amber-400 font-medium mb-2">Counter Offer</p>
                  {bidDetails.bid.counter_amount && (
                    <p className="text-white">Amount: {formatCurrency(bidDetails.bid.counter_amount)}</p>
                  )}
                  {bidDetails.bid.counter_message && (
                    <p className="text-slate-300 text-sm mt-2">{bidDetails.bid.counter_message}</p>
                  )}
                </div>
              )}

              {/* Admin Rejection */}
              {bidDetails.bid?.admin_rejected && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 font-medium mb-1">Admin Rejected</p>
                  <p className="text-slate-300 text-sm">{bidDetails.bid.admin_rejection_reason}</p>
                  {bidDetails.bid.rejected_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Rejected on {formatDate(bidDetails.bid.rejected_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-700/50 rounded-xl p-3">
                  <p className="text-slate-400">Created</p>
                  <p className="text-white">{formatDate(bidDetails.bid?.created_at)}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-3">
                  <p className="text-slate-400">Last Updated</p>
                  <p className="text-white">{formatDate(bidDetails.bid?.updated_at || bidDetails.bid?.created_at)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Bid Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="reject-bid-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              Reject Bid
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-slate-300">
              Are you sure you want to reject this bid from <strong>{selectedBid?.bidder_name}</strong>? 
              The bidder will be notified.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Reason for rejection *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide a reason for rejecting this bid..."
                className="w-full bg-slate-700 border border-slate-600 rounded-xl p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-red-500"
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRejectBid}
                disabled={actionLoading || !rejectReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-reject-btn"
              >
                {actionLoading ? 'Rejecting...' : 'Reject Bid'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
